import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import './Auth.css';
import { API_URL } from '../../config/api';
import { showError } from '../../utils/toast';
import { unwrapApiPayload } from '../../utils/auth';
import { fitGoogleButtonOverlay, renderGoogleOverlayButton } from './googleButtonOverlay';
import { getPostLoginPath } from '../../super-admin/auth';
import PhoneField, { EMPTY_PHONE, phoneErrorFrom, toE164Phone } from './PhoneField';

const ENV_GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
const LOGO_SRC = `${process.env.PUBLIC_URL || ''}/brand/tracktcrm-auth-logo.png`;

const GoogleIcon = () => (
  <svg className="google-icon" width="18" height="18" viewBox="0 0 18 18" aria-hidden>
    <path fill="#4285F4" d="M16.51 8H8.98v3h4.3c-.18 1-.74 1.48-1.6 2.04v2.01h2.6a7.8 7.8 0 0 0 2.38-5.88z" />
    <path fill="#34A853" d="M8.98 17c2.16 0 3.97-.72 5.3-1.94l-2.6-2a4.8 4.8 0 0 1-7.18-2.54H1.83v2.07A8 8 0 0 0 8.98 17z" />
    <path fill="#FBBC05" d="M4.5 10.52a4.8 4.8 0 0 1 0-3.04V5.41H1.83a8 8 0 0 0 0 7.18z" />
    <path fill="#EA4335" d="M8.98 4.18c1.17 0 2.23.4 3.06 1.2l2.3-2.3A8 8 0 0 0 1.83 5.4L4.4 7.49a4.77 4.77 0 0 1 4.58-3.3z" />
  </svg>
);

const MailIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
);

const LockIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
    <rect x="3" y="11" width="18" height="11" rx="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const UserIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const getPasswordChecks = (password) => ({
  minLength: password.length >= 8,
  hasUppercase: /[A-Z]/.test(password),
  hasLowercase: /[a-z]/.test(password),
  hasSpecialCharacter: /[^A-Za-z0-9]/.test(password)
});

const getPasswordStrength = (score) => {
  if (score <= 1) return 'Weak';
  if (score <= 3) return 'Medium';
  return 'Strong';
};

const isValidEmail = (value) => {
  const email = String(value).trim();
  if (!email) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

function Register() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [phone, setPhone] = useState(EMPTY_PHONE);
  const [phoneError, setPhoneError] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [googleClientId, setGoogleClientId] = useState('');
  const [googleConfigLoaded, setGoogleConfigLoaded] = useState(false);
  const googleButtonRef = useRef(null);
  const navigate = useNavigate();
  const passwordChecks = getPasswordChecks(formData.password);
  const passwordScore = Object.values(passwordChecks).filter(Boolean).length;
  const isPasswordStrong = passwordScore === 4;
  const passwordStrength = getPasswordStrength(passwordScore);
  const strengthLevel = passwordStrength === 'Strong' ? 3 : passwordStrength === 'Medium' ? 2 : 1;

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // The backend is the source of truth so the sign-in client can change without rebuilding the frontend.
  useEffect(() => {
    fetch(`${API_URL}/auth/config`, { method: 'GET' })
      .then((res) => (res.ok ? res.json() : {}))
      .then(unwrapApiPayload)
      .then((data) => {
        const id = (data && data.googleClientId && String(data.googleClientId).replace(/^["']|["']$/g, '').trim()) || '';
        setGoogleClientId(id || ENV_GOOGLE_CLIENT_ID);
      })
      .catch(() => setGoogleClientId(ENV_GOOGLE_CLIENT_ID))
      .finally(() => setGoogleConfigLoaded(true));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const name = formData.name.trim();
    const email = formData.email.trim();

    if (!name) {
      showError('Please enter your full name');
      return;
    }

    if (!email) {
      showError('Please enter your email address');
      return;
    }

    if (!isValidEmail(email)) {
      showError('Please enter a valid email address');
      return;
    }

    const e164Phone = toE164Phone(phone);
    if (!phone.number.trim()) {
      showError('Please enter your mobile number');
      return;
    }
    if (!e164Phone) {
      showError('Please enter a valid mobile number');
      return;
    }

    if (!formData.password) {
      showError('Please enter a password');
      return;
    }

    if (!formData.confirmPassword) {
      showError('Please confirm your password');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      showError('Passwords do not match');
      return;
    }

    if (!isPasswordStrong) {
      showError('Password must be at least 8 characters and include uppercase, lowercase, and one special character');
      return;
    }

    if (!agreed) {
      showError('Please accept the Terms of Service and Privacy Policy');
      return;
    }

    setLoading(true);

    try {
      const payload = {
        name,
        email,
        phone: e164Phone,
        password: formData.password
      };

      const response = await fetch(`${API_URL}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.ok && data.verifyBy === 'phone') {
        navigate('/verify-otp', {
          state: {
            email: data.email || email,
            verifyBy: 'phone',
            phone: data.phone || e164Phone,
            channel: data.channel,
            resendInSeconds: data.resendInSeconds,
          },
        });
      } else if (response.ok) {
        navigate('/verify-otp', { state: { email: data.email || email } });
      } else {
        const takenMessage = phoneErrorFrom(data);
        if (takenMessage) setPhoneError(takenMessage);
        showError(takenMessage || data.message || data.error || 'Registration failed');
      }
    } catch (error) {
      showError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!googleClientId) {
      return;
    }

    const handleGoogleCredential = async (response) => {
      if (!response?.credential) {
        showError('Google sign-up was cancelled. Please try again.');
        return;
      }

      setLoading(true);

      try {
        const res = await fetch(`${API_URL}/auth/google`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ credential: response.credential }),
        });

        const data = await res.json();

        if (res.ok) {
          localStorage.setItem('token', data.token);
          localStorage.setItem('user', JSON.stringify(data.user));
          navigate(getPostLoginPath(data.user));
        } else {
          showError(data.error || 'Google sign-up failed');
        }
      } catch (error) {
        showError('Network error during Google sign-up. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    const initializeGoogle = () => {
      if (window.google?.accounts?.id && googleButtonRef.current) {
        window.google.accounts.id.initialize({
          client_id: googleClientId,
          callback: handleGoogleCredential,
        });
        renderGoogleOverlayButton(googleButtonRef.current);
      }
    };
    const handleResize = () => fitGoogleButtonOverlay(googleButtonRef.current);
    window.addEventListener('resize', handleResize);

    const cleanup = () => {
      window.removeEventListener('resize', handleResize);
      window.google?.accounts?.id?.cancel();
    };

    const existingScript = document.getElementById('google-identity-service');
    if (existingScript) {
      if (window.google) {
        initializeGoogle();
      } else {
        existingScript.addEventListener('load', initializeGoogle, { once: true });
      }
      return cleanup;
    }

    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.id = 'google-identity-service';
    script.onload = initializeGoogle;
    document.body.appendChild(script);

    return cleanup;
  }, [navigate, googleClientId]);

  // Keyboard fallback only; mouse clicks land on the overlaid Google button.
  const triggerGoogleSignIn = () => {
    window.google?.accounts?.id?.prompt();
  };

  return (
    <div className="auth-container">
      <div className="auth-form-section">
        <div className="auth-form-container">
          <a className="auth-logo" href="https://tracktcrm.com/" target="_blank" rel="noopener noreferrer" aria-label="TracktCRM">
            <img src={LOGO_SRC} alt="TracktCRM" />
          </a>

          <div className="auth-header">
            <h1>Create your free TracktCRM account</h1>
            <p>Free forever for 1 user. No credit card needed.</p>
          </div>

          <div className="social-login">
            {googleClientId ? (
              <div className="google-btn-wrap">
                <button
                  type="button"
                  className="google-auth-btn"
                  onClick={triggerGoogleSignIn}
                  disabled={loading}
                >
                  <GoogleIcon />
                  Sign up with Google
                </button>
                <div
                  ref={googleButtonRef}
                  className={`google-gis-host${loading ? ' is-disabled' : ''}`}
                  aria-hidden
                />
              </div>
            ) : googleConfigLoaded ? (
              <p className="google-hint">
                Google sign-up is not configured. Set <code>REACT_APP_GOOGLE_CLIENT_ID</code> or <code>GOOGLE_CLIENT_ID</code>.
              </p>
            ) : (
              <div className="social-button-wrapper google-loading">Loading…</div>
            )}
          </div>

          <div className="auth-divider">
            <span>OR</span>
          </div>

          <form onSubmit={handleSubmit} className="auth-form" noValidate>
            <div className="form-group">
              <label htmlFor="name">Full Name*</label>
              <div className="input-with-icon">
                <span className="input-leading-icon"><UserIcon /></span>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter your full name"
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="email">Email address*</label>
              <div className="input-with-icon">
                <span className="input-leading-icon"><MailIcon /></span>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter your email"
                  required
                />
              </div>
            </div>

            <PhoneField
              id="phone"
              value={phone}
              onChange={(next) => {
                setPhone(next);
                setPhoneError('');
              }}
              error={phoneError}
              disabled={loading}
            />

            <div className="form-group">
              <label htmlFor="password">Password*</label>
              <div className="password-input-wrapper input-with-icon">
                <span className="input-leading-icon"><LockIcon /></span>
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Create a password"
                  required
                />
                <span
                  className="password-toggle-icon"
                  onClick={() => setShowPassword(!showPassword)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setShowPassword(!showPassword);
                    }
                  }}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                      <line x1="1" y1="1" x2="23" y2="23"></line>
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                      <circle cx="12" cy="12" r="3"></circle>
                    </svg>
                  )}
                </span>
              </div>
              <div className="password-checklist">
                <div className="password-strength-meter">
                  <div className={`strength-segment ${formData.password && strengthLevel >= 1 ? `active ${passwordStrength.toLowerCase()}` : ''}`} />
                  <div className={`strength-segment ${formData.password && strengthLevel >= 2 ? `active ${passwordStrength.toLowerCase()}` : ''}`} />
                  <div className={`strength-segment ${formData.password && strengthLevel >= 3 ? `active ${passwordStrength.toLowerCase()}` : ''}`} />
                </div>
                <p className={`password-strength-text ${formData.password ? passwordStrength.toLowerCase() : ''}`}>
                  {formData.password ? passwordStrength : ''}
                </p>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password*</label>
              <div className="password-input-wrapper input-with-icon">
                <span className="input-leading-icon"><LockIcon /></span>
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Confirm your password"
                  required
                />
                <span
                  className="password-toggle-icon"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setShowConfirmPassword(!showConfirmPassword);
                    }
                  }}
                  aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
                >
                  {showConfirmPassword ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                      <line x1="1" y1="1" x2="23" y2="23"></line>
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                      <circle cx="12" cy="12" r="3"></circle>
                    </svg>
                  )}
                </span>
              </div>
            </div>

            <label className="auth-agree">
              <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} required />
              <span>
                I agree to the{' '}
                <a href="https://tracktcrm.com/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>
                {' '}and{' '}
                <a href="https://tracktcrm.com/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
              </span>
            </label>

            <button type="submit" className="auth-button" disabled={loading}>
              {loading ? 'Creating account...' : 'Register'}
            </button>
          </form>

          <a href="https://tracktcrm.com/" className="auth-back-link" target="_blank" rel="noopener noreferrer">
            ← Back to Home Page
          </a>

          <div className="auth-footer">
            <p>
              Already have an account? <Link to="/login">Login</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Register;

